//LIBRARIES
import React, { Component } from 'react';
import MediaQuery from 'react-responsive';
import { connect } from 'react-redux';
import { localize } from 'react-localize-redux';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import { MuiThemeProvider } from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import { createMuiTheme } from '@material-ui/core/styles';
import { MdWarning as WarningIcon } from 'react-icons/md';
import jstz from 'jstz';
import camelCase from 'lodash/camelCase';

//HELPERS
import {
  StyledHeader,
  StyledMain,
  StyledHeaderContainer,
  StyledTableContainer,
} from 'helpers/StyledTableComponentsHelper';
import { isFilterInStrings } from 'helpers/StringHelper.js';
import { AccentColor } from 'helpers/AccentColorHelper';
import { drawerStyle } from 'helpers/tableHelpers';
import { browserHistory } from 'react-router';
import { hostIsCapreit } from 'helpers/CurrentWindowLocation';
import { formatPhone, timeFromNow, toFriendlyDateString } from 'helpers/Formatter';
import { featureEnabled } from 'helpers/ToggleBot';
import { colors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';

//ACTIONS
import * as landlordTenantsActions from 'actions/landlordTenantsActions';
import { openEditLease } from 'actions/LeaseDetailsActions';
import { getProperties, getUnits } from 'actions/landlordPropertiesActions';
import { getTicket, deleteTicket } from 'actions/maintenanceTicketActions';
import {
  getAssets,
  createOrUpdateMaintenanceTicket,
  createOrUpdateMaintenanceTask,
  deleteTask,
  getMaintenanceCategories,
  getMaintenanceLocations,
} from 'actions/maintenanceActions';
import { getGroupedVendors } from 'actions/vendorsActions';
import { getCompanyPreferences } from 'actions/companyDetailsActions';
import { getChatWindowMessages } from 'actions/chatActions';

//COMPONENTS
import TenantOverview from './components/TenantOverview';
import HeaderWithPeriod from 'components/shared/HeaderWithPeriod';
import TenantResidentVerbiageHelper from 'components/shared/TenantResidentVerbiageHelper';
import TicketDetailsOverview from '../maintenance/components/tickets/TicketDetailsOverview';
import ChatDrawer from 'components/tenant_app/components/ChatDrawer';
import TabbedTable from 'components/ui/TabbedTable';
import MobileTable from 'components/ui/MobileTable';
import TableFilters from 'components/ui/TableFilters';
import StatusTag from 'components/ui/StatusTag';
import AntDrawer from 'components/ui/Drawer';
import { RenderIfToggleOn, RenderIfToggleOff } from 'components/shared/ToggleRenderer';
import TicketViewing from 'components/landlord_app/maintenance/tickets/TicketViewing/TicketViewing';
import ResidentDrawer from './components/ResidentDrawer';
import * as residentDrawerActions from './components/ResidentDrawer/store/actions';

export default withMobileDialog()(
  connect(
    (store) => ({
      tenants: store.landlordTenants.tenants,
      fetchingTenants: store.landlordTenants.fetchingTenants,
      tenantsCounts: store.landlordTenants.tenantsCounts,
      searchTotalResultsCount: store.landlordTenants.searchTotalResultsCount,
      tenantDetails: store.landlordTenants.tenantDetails,
      padDetail: store.landlordTenants.padDetail,
      properties: store.landlordProperties.properties,
      documentUploaderOpen: store.landlordTenants.documentUploaderDialogOpen,
      ticketDetails: store.maintenanceTicket.ticketDetails,
      companyPreferences: store.companyDetails.companyPreferences,
      propertyTypes: store.userTypes.propertyTypes,
      isUserAdmin: store.userTypes.admin,
      currentLanguage: store.locale.currentLanguage,
    }),
    {
      ...landlordTenantsActions,
      openEditLease,
      getProperties,
      getTicket,
      getUnits,
      getAssets,
      getGroupedVendors,
      createOrUpdateMaintenanceTicket,
      getCompanyPreferences,
      createOrUpdateMaintenanceTask,
      deleteTask,
      deleteTicket,
      getChatWindowMessages,
      getMaintenanceCategories,
      getMaintenanceLocations,
      openResidentDrawer: residentDrawerActions.openResidentDrawer,
    }
  )(
    localize()(
      class LandlordTenants extends Component {
        constructor(props) {
          super(props);
          this.state = {
            showMaintenanceDrawer: false,
            viewTicketDrawerOpen: false,
            showChatDrawer: false,
            filtersActive: false,
            filters: {
              search: '',
              properties: Array(this.props.properties.length).fill(false),
              statuses: Array(this.statuses.length).fill(false),
              userTypes: Array(2).fill(false),
            },
            drawerVisible: false,
            currentTab: 'current',
            sortKey: '',
            sortOrder: '',
            offset: 0,
            timezone: 'America/Toronto',
          };
        }

        userTypes = [
          this.props.propertyTypes === 'commercial' ? 'resident' : 'tenant',
          'guarantor',
        ];

        statuses = () => {
          const showOptedOut = featureEnabled('ui.residents.account.show_opt_out');
          if (!showOptedOut) {
            return ['active', 'invited', 'not_invited', 'inactive'];
          }
          return ['active', 'invited', 'not_invited', 'inactive', 'opted_out'];
        };

        setRef = (ref) => (this.table = ref);

        fetchTenantsAndStats = () => {
          this.props.getTenantsStats();
          this.fetchTenants();
        };

        componentDidMount = () => {
          this.fetchTenantsAndStats();
          this.props.getProperties({ for_multiselect: true });
          this.props.getCompanyPreferences();

          const query = browserHistory.getCurrentLocation().query;
          const tenantId = query && query.tenantId;
          if (tenantId) {
            this.toggleDrawer(tenantId);
          }
          const timezone = jstz.determine();
          this.setState({
            timezone: timezone.name() || 'America/Toronto',
          });
        };

        toggleDrawer = (tenantID) => {
          if (!this.state.showDrawer && tenantID) {
            this.props.getTenant(tenantID, { all_maintenance_tickets: true }).then(() => {
              this.props.getTenantPadDetail(tenantID);
              this.props.getLeadDocuments(this.props.tenantDetails.id);
              this.props.getCotenantPayments(this.props.tenantDetails.id, false);
              this.setState({
                showDrawer: true,
              });
              browserHistory.push(`/companies/tenants?tenantId=${tenantID}`);

              this.props.openResidentDrawer();
            });
            return;
          }

          this.setState({
            showDrawer: false,
          });
          browserHistory.push(`/companies/tenants`);
        };

        openAnotherTenant = (tenantId) => {
          this.setState(
            {
              showDrawer: false,
            },
            () => {
              this.toggleDrawer(tenantId);
            }
          );
        };

        reloadTenant = () => {
          this.fetchTenantsAndStats();
          this.props.getTenant(this.props.tenantDetails.id, {
            all_maintenance_tickets: true,
          });
        };

        filterLeaseWithString = (filterBy) => {
          return this.props.tenants.filter((item) =>
            isFilterInStrings(
              [item.full_name, item.email, item.phone_number, item.unit, item.property_name],
              filterBy
            )
          );
        };

        inviteTenant = async () => {
          await this.props.inviteLead(this.props.tenantDetails.lead_id);
          this.props.getTenantsStats();
          this.fetchTenants(true);
          this.props.getTenant(this.props.tenantDetails.id, {
            all_maintenance_tickets: true,
          });
        };

        optOutLead = async () => {
          await this.props.optOutLead(this.props.tenantDetails.lead_id);
          this.props.getTenantsStats();
          this.fetchTenants(true);
        };

        optInLead = async () => {
          await this.props.optInLead(this.props.tenantDetails.lead_id);
          this.props.getTenantsStats();
          this.fetchTenants(true);
        };

        deleteTenant = async () => {
          await this.props.deleteCotenant(
            this.props.tenantDetails.lease.id,
            this.props.tenantDetails.id
          );
          this.setState(
            {
              disabledAddNoticeSubmit: false,
              offset: 0,
            },
            () => this.fetchTenantsAndStats()
          );
        };

        deletePadDetail = async () => {
          const cotentantId = this.props.tenantDetails.id;
          await this.props.deleteTenantPadDetail(cotentantId);
          await this.props.getTenant(cotentantId, {
            all_maintenance_tickets: true,
          });
          await this.props.getTenantPadDetail(cotentantId);
        };

        addTenantDocument = async (formData) => {
          await this.props.createDocument(formData, this.props.tenantDetails.lease.id);
          this.props.getLeaseDocuments(this.props.tenantDetails.lease.id);
        };

        toggleOldTicketViewDrawer = async (maintenanceTicketID) => {
          if (!this.state.showMaintenanceDrawer && maintenanceTicketID) {
            await this.props.getTicket(maintenanceTicketID);
            let assetParams = {
              property_id: this.props.ticketDetails.property_id,
              for_multiselect: true,
            };

            const unitId = this.props.ticketDetails.unit_id;

            if (unitId) {
              assetParams = { ...assetParams, unit_id: unitId };
            }
            this.props.getAssets(assetParams);
            this.props.getMaintenanceCategories(this.props.ticketDetails.property_id);
            this.props.getMaintenanceLocations(this.props.ticketDetails.property_id);
            this.props.getUnits(this.props.ticketDetails.property_id, {
              for_multiselect: true,
            });
            this.setState({
              showMaintenanceDrawer: true,
            });
            return;
          }
          this.setState({ showMaintenanceDrawer: false });
        };

        openMaintenanceTicketDrawer = async (ticketID) => {
          if (!featureEnabled('ui.maintenance.new_tickets_ui')) {
            this.toggleOldTicketViewDrawer(ticketID);
            return;
          }

          await this.props.getTicket(ticketID);
          this.setState({ viewTicketDrawerOpen: true });
        };

        closeMaintenanceTicketDrawer = () => {
          if (!featureEnabled('ui.maintenance.new_tickets_ui')) {
            this.toggleOldTicketViewDrawer();
            return;
          }

          this.setState({ viewTicketDrawerOpen: false });
        };

        updateTicket = (formData, ticketID) => {
          let ticketId = formData.id || ticketID;
          return this.props.createOrUpdateMaintenanceTicket(formData, ticketId).then(() => {
            this.openAnotherTenant(this.props.tenantDetails.id);
          });
        };

        deleteTicket = () => {
          return this.props.deleteTicket(this.props.ticketDetails.id).then(() => {
            this.openAnotherTenant(this.props.tenantDetails.id);
            this.closeMaintenanceTicketDrawer();
          });
        };

        createOrUpdateTask = (task, taskID) => {
          let taskId = task.id || taskID;
          this.props.createOrUpdateMaintenanceTask(task, taskId, this.props.ticketDetails.id);
        };

        deleteTask = (taskId) => {
          this.props.deleteTask(taskId, this.props.ticketDetails.id);
        };

        toggleChatDrawer = () => {
          this.setState({
            showChatDrawer: !this.state.showChatDrawer,
          });
        };

        filterProperties = (booleanArrayMask) => {
          return this.props.properties.filter((_, i) => booleanArrayMask[i]);
        };

        filterStatuses = (booleanArrayMask) => {
          return this.statuses().filter((_, i) => booleanArrayMask[i]);
        };

        filterUserTypes = (booleanArrayMask) => {
          return this.userTypes.filter((_, i) => booleanArrayMask[i]);
        };

        handleClearSearch = (setFieldValue) => {
          this.setState(
            {
              filtersActive: false,
              filters: {
                search: '',
                properties: Array(this.props.properties.length).fill(false),
                statuses: Array(this.statuses().length).fill(false),
                userTypes: Array(2).fill(false),
              },
              offset: 0,
            },
            () => this.fetchTenants()
          );
          if (this.table) this.table.scrollToTop(0);
          setFieldValue('search', '');
          setFieldValue('properties', Array(this.props.properties.length).fill(false));
          setFieldValue('statuses', Array(this.statuses().length).fill(false));
          setFieldValue('userTypes', Array(2).fill(false));
        };

        handleFilters = (values) => {
          const isFiltersUntouched =
            values.search === '' &&
            !values.properties.includes(true) &&
            !values.statuses.includes(true) &&
            !values.userTypes.includes(true);
          const newState = {
            filtersActive: !isFiltersUntouched,
            filters: {
              search: values.search,
              properties: values.properties,
              statuses: values.statuses,
              userTypes: values.userTypes,
            },
            drawerVisible: false,
            offset: 0,
          };
          if (this.table) this.table.scrollToTop(0);
          this.setState(newState, () => this.fetchTenants());
        };

        columns = [
          {
            title: `${
              this.props.translate('landlordPortal.landlordTenants.status').props.children
            }`,
            dataKey: 'status',
            sortable: false,
            width: 300,
            render: ({ data }) => <StatusTag status={data} />,
          },
          {
            title: `${this.props.translate('landlordPortal.landlordTenants.unit').props.children}`,
            dataKey: 'unit',
            sortable: true,
            width: 200,
          },
          {
            title: `${
              this.props.translate('landlordPortal.landlordTenants.fullName').props.children
            }`,
            dataKey: 'full_name',
            sortable: true,
            width: 400,
          },
          {
            title: `${this.props.translate('landlordPortal.landlordTenants.email').props.children}`,
            dataKey: 'email',
            sortable: true,
            width: 500,
          },
          {
            title: `${
              this.props.translate('landlordPortal.landlordTenants.phoneNumber').props.children
            }`,
            dataKey: 'phone_number',
            sortable: false,
            width: 400,
          },
          {
            title: `${
              this.props.translate('landlordPortal.landlordTenants.lastActive').props.children
            }`,
            dataKey: 'last_active',
            sortable: true,
            width: 200,
            render: ({ data }) =>
              timeFromNow(data, {
                language: this.props.currentLanguage,
                timezone: this.state.timezone,
              }) || '-',
          },
          {
            title: `${this.props.translate('landlordPortal.landlordTenants.type').props.children}`,
            dataKey: 'is_guarantor',
            sortable: false,
            width: 200,
            render: ({ data }) =>
              data ? (
                this.props.translate('landlordPortal.landlordTenants.userTypes.guarantor').props
                  .children
              ) : (
                <TenantResidentVerbiageHelper capitalize />
              ),
            hide: hostIsCapreit(),
          },
          {
            title: `${
              this.props.translate('landlordPortal.landlordTenants.insuranceExpiryDate').props
                .children
            }`,
            dataKey: 'insurance_expiry_date',
            sortable: true,
            width: 450,
            render: ({ data }) =>
              data
                ? toFriendlyDateString(data)
                : this.props.translate('landlordPortal.landlordTenants.none'),
          },
        ];

        handleOpenDrawer = () => {
          this.setState({
            drawerVisible: true,
          });
        };

        handleDrawerClose = () => {
          this.setState({
            drawerVisible: false,
          });
        };

        handleTabChange = (tabName, _queryObj) => {
          this.setState(
            {
              currentTab: tabName,
              offset: 0,
            },
            () => {
              this.fetchTenants();
            }
          );
          if (this.table) this.table.scrollToTop(0);
        };

        handleSort = (key, order) => {
          this.setState(
            {
              sortKey: key,
              sortOrder: order,
              offset: 0,
            },
            () => {
              this.fetchTenants();
            }
          );
          if (this.table) this.table.scrollToTop(0);
        };

        fetchTenants = (updateList = false) => {
          let { filters, filtersActive, currentTab, offset, sortKey, sortOrder } = this.state;

          let fetchPromise = null;

          let filterOptions = {
            sort_key: sortKey,
            sort_order: sortOrder,
          };

          if (updateList) {
            offset = 0;
          }

          if (filtersActive) {
            const propertiesToFilter = this.filterProperties(filters.properties).map(
              (data) => data.value
            );
            const statusesToFilter = this.filterStatuses(filters.statuses);
            const userTypesToFilter = this.filterUserTypes(filters.userTypes);

            filterOptions = {
              query: filters.search,
              property_ids: propertiesToFilter,
              statuses: statusesToFilter,
              user_types: userTypesToFilter,
              sort_key: sortKey,
              sort_order: sortOrder,
            };

            fetchPromise = this.props.getFilteredTenants('all', offset, filterOptions);
          } else {
            fetchPromise = this.props.getFilteredTenants(currentTab, offset, filterOptions);
          }

          fetchPromise.then(() => {
            this.setState({
              offset: this.props.tenants.length,
            });
          });
        };

        accentColor = AccentColor();

        render() {
          const { filters } = this.state;
          const { tenants, tenantsCounts, searchTotalResultsCount } = this.props;

          let filterOptions = [
            {
              useSearch: true,
              options: this.props.properties.map((data) => data.label),
              name: 'properties',
              title: `${
                this.props.translate('landlordPortal.landlordTenants.filterByProperties').props
                  .children
              }`,
            },
            {
              useSearch: false,
              options: this.statuses().map(
                (statusCode) =>
                  this.props.translate(`landlordPortal.landlordTenants.statuses.${statusCode}`)
                    .props.children
              ),
              name: 'statuses',
              title: `${
                this.props.translate('landlordPortal.landlordTenants.filterByResidentStatus').props
                  .children
              }`,
            },
          ];

          // TODO: TOGGLEBOT toggle for hiding filter by user type
          if (!hostIsCapreit()) {
            filterOptions.push({
              useSearch: false,
              options: this.userTypes.map((userType) => {
                if (this.props.propertyTypes !== 'commercial') {
                  userType = userType.replace(/tenant/gi, 'Resident');
                }
                return this.props.translate(
                  `landlordPortal.landlordTenants.userTypes.${camelCase(userType)}`
                ).props.children;
              }),
              name: 'userTypes',
              title: `${
                this.props.translate('landlordPortal.landlordTenants.filterByUserType').props
                  .children
              }`,
            });
          }

          const tabs = [
            {
              title: `${
                this.props.translate('landlordPortal.landlordTenants.current').props.children
              }`,
              label: `${
                this.props.translate('landlordPortal.landlordTenants.current').props.children
              }`,
              value: 'current',
              count: tenantsCounts['current'] || 0,
              onClick: () => this.handleTabChange('current', { subset: 'current' }),
            },
            {
              title: `${
                this.props.translate('landlordPortal.landlordTenants.unregistered').props.children
              }`,
              label: `${
                this.props.translate('landlordPortal.landlordTenants.unregistered').props.children
              }`,
              value: 'unregistered',
              count: tenantsCounts['unregistered'] || 0,
              onClick: () =>
                this.handleTabChange('unregistered', {
                  subset: 'unregistered',
                }),
            },
            {
              title: `${
                this.props.translate('landlordPortal.landlordTenants.future').props.children
              }`,
              label: `${
                this.props.translate('landlordPortal.landlordTenants.future').props.children
              }`,
              value: 'future',
              count: tenantsCounts['future'] || 0,
              onClick: () => this.handleTabChange('future', { subset: 'future' }),
            },
            {
              title: `${
                this.props.translate('landlordPortal.landlordTenants.prior').props.children
              }`,
              label: `${
                this.props.translate('landlordPortal.landlordTenants.prior').props.children
              }`,
              value: 'prior',
              count: tenantsCounts['prior'] || 0,
              onClick: () => this.handleTabChange('prior', { subset: 'prior' }),
            },
            {
              title: `${this.props.translate('landlordPortal.landlordTenants.all').props.children}`,
              label: `${this.props.translate('landlordPortal.landlordTenants.all').props.children}`,
              value: 'all',
              count: tenantsCounts['all'] || 0,
              onClick: () => this.handleTabChange('all', { subset: 'all' }),
            },
          ];

          const filteredData = tenants.map((datum) => {
            if (datum.phone_number) {
              return {
                ...datum,
                phone_number: formatPhone(datum.phone_number),
              };
            } else {
              return datum;
            }
          });

          return (
            <StyledMain>
              <StyledHeaderContainer>
                <StyledHeader>
                  <HeaderWithPeriod
                    headerText={
                      <span>
                        <TenantResidentVerbiageHelper capitalize />s
                      </span>
                    }
                    newStyle
                  />
                </StyledHeader>
                <TableFilters
                  filters={filters}
                  handleSubmitFilters={this.handleFilters}
                  handleClearFilters={this.handleClearSearch}
                  filterOptions={filterOptions}
                  accentColor={this.accentColor}
                />
              </StyledHeaderContainer>

              <StyledTableContainer>
                {this._renderTableView({
                  filteredData,
                  tabs,
                  searchTotalResultsCount,
                })}
                {this._renderMobileView({
                  filteredData,
                  tabs,
                  currentTab: this.state.currentTab,
                  tabChange: this.handleTabChange,
                })}
              </StyledTableContainer>
              {this._renderViewTicketDrawer()}
              {this._renderOldTicketDrawer()}
              {this._renderChatDrawer()}
              {this._renderLeaseDetails()}
            </StyledMain>
          );
        }

        _renderOldTicketDrawer = () => (
          <RenderIfToggleOff toggle="ui.maintenance.new_tickets_ui">
            <MuiThemeProvider theme={createMuiTheme(drawerStyle)}>
              <Drawer
                open={this.state.showMaintenanceDrawer}
                onClose={this.closeMaintenanceTicketDrawer}
                anchor="right"
              >
                <TicketDetailsOverview
                  toggleDrawer={this.toggleOldTicketViewDrawer}
                  maintenanceTicket={this.props.ticketDetails}
                  updateTicket={this.updateTicket}
                  hideDelete={['cancelled_dup', 'cancelled', 'cancelled_no_res'].includes(
                    this.props.ticketDetails
                  )}
                  companyPreferences={this.props.companyPreferences}
                  createOrUpdateTask={this.createOrUpdateTask}
                  deleteTask={this.deleteTask}
                  deleteAction={this.deleteTicket}
                />
              </Drawer>
            </MuiThemeProvider>
          </RenderIfToggleOff>
        );

        _renderViewTicketDrawer = () => (
          <RenderIfToggleOn toggle="ui.maintenance.new_tickets_ui">
            <AntDrawer
              maskClosable={false}
              keyboard
              visible={this.state.viewTicketDrawerOpen}
              onClose={this.closeMaintenanceTicketDrawer}
              closable={false}
              width={600}
            >
              {this.state.viewTicketDrawerOpen && (
                <TicketViewing
                  handleClose={this.closeMaintenanceTicketDrawer}
                  selectedTicket={this.props.ticketDetails}
                  onSubmissionComplete={() => this.props.getTicket(this.props.ticketDetails.id)}
                />
              )}
            </AntDrawer>
          </RenderIfToggleOn>
        );

        _renderChatDrawer = () => {
          return (
            <MuiThemeProvider theme={createMuiTheme(drawerStyle)}>
              <Drawer
                open={this.state.showChatDrawer}
                onClose={this.toggleChatDrawer}
                anchor="right"
              >
                <ChatDrawer
                  cotenantId={this.props.tenantDetails.id}
                  toggleDrawer={this.toggleChatDrawer}
                />
              </Drawer>
            </MuiThemeProvider>
          );
        };

        _renderLeaseDetails = () => {
          const shouldOpen =
            !this.state.viewTicketDrawerOpen &&
            !this.state.showMaintenanceDrawer &&
            !this.state.showChatDrawer &&
            !this.state.showLeaseDrawer &&
            this.state.showDrawer;

          if (featureEnabled('ui.residents.new_resident_drawer')) {
            return (
              <ResidentDrawer
                onClose={() => {
                  browserHistory.push(`/companies/tenants`);
                  this.setState({ showDrawer: false });
                }}
                reloadPageData={() => {
                  this.props.getTenantsStats();
                  this.fetchTenants(true);
                }}
                toggleChatDrawer={this.toggleChatDrawer}
              />
            );
          }

          return (
            <AntDrawer
              visible={shouldOpen}
              onClose={this.toggleDrawer}
              width={550}
              closable={false}
            >
              <TenantOverview
                toggleDrawer={this.toggleDrawer}
                tenantDetails={this.props.tenantDetails}
                padDetail={this.props.padDetail}
                deletePadDetail={this.deletePadDetail}
                inviteTenant={this.inviteTenant}
                optOutLead={this.optOutLead}
                optInLead={this.optInLead}
                reloadTenant={this.reloadTenant}
                deleteTenant={this.deleteTenant}
                addTenantDocument={this.addTenantDocument}
                toggleAddDocumentDialog={this.props.toggleDocumentUploaderDialogOpen}
                documentIsOpen={this.props.documentUploaderOpen}
                openAnotherTenant={this.openAnotherTenant}
                goToTicket={this.openMaintenanceTicketDrawer}
                goToChat={this.toggleChatDrawer}
              />
            </AntDrawer>
          );
        };

        _renderTableView = ({ filteredData, tabs, searchTotalResultsCount }) => {
          let groupBy = 'property_name';
          if (featureEnabled('ui.properties.namespace_by_city')) {
            groupBy = 'property_name_city';
          }
          return (
            <MediaQuery query="(min-device-width: 750px)">
              <TabbedTable
                setRef={this.setRef}
                groupRowsBy={groupBy}
                tabs={tabs}
                columns={this.getEnhancedColumns()}
                data={filteredData}
                loadMoreData={this.fetchTenants}
                isLoading={this.props.fetchingTenants}
                filtersActive={this.state.filtersActive}
                onRowClick={(data) => {
                  this.toggleDrawer(data.original_id);
                }}
                onColumnClick={(data) => {
                  this.handleSort(data.key, data.order);
                }}
                searchTotalResultsCount={searchTotalResultsCount}
              />
            </MediaQuery>
          );
        };

        _renderMobileView = ({ filteredData, tabs, currentTab, tabChange }) => {
          let groupBy = 'property_name';
          if (featureEnabled('ui.properties.namespace_by_city')) {
            groupBy = 'property_name_city';
          }
          return (
            <MediaQuery query="(max-device-width: 750px)">
              <MobileTable
                groupRowsBy={groupBy}
                data={filteredData}
                tabs={tabs}
                tabIndex={currentTab}
                tabChange={tabChange}
                filtersActive={this.state.filtersActive}
                columns={this.columns}
                title="full_name"
                tag="status"
                stickyHeaderTop={this.state.filters.search ? '137px' : '107px'}
                fieldsAboveFold={[
                  {
                    title: `${
                      this.props.translate('landlordPortal.landlordTenants.unit').props.children
                    }`,
                    key: 'unit',
                  },
                ]}
                fieldsBelowFold={[
                  {
                    title: `${
                      this.props.translate('landlordPortal.landlordTenants.email').props.children
                    }`,
                    key: 'email',
                  },
                  {
                    title: `${
                      this.props.translate('landlordPortal.landlordTenants.phoneNumber').props
                        .children
                    }`,
                    key: 'phone_number',
                  },
                  {
                    title: `${
                      this.props.translate('landlordPortal.landlordTenants.lastActive').props
                        .children
                    }`,
                    key: 'last_active',
                  },
                ]}
                onRowClick={(data) => {
                  this.toggleDrawer(data.id);
                }}
              />
            </MediaQuery>
          );
        };

        getEnhancedColumns = () => {
          if (featureEnabled('bounce_emails')) {
            return [
              {
                title: getTranslatedString('landlordPortal.landlordTenants.attentionRequired'),
                dataKey: 'attention_required',
                sortable: false,
                width: 230,
                render: ({ data }) => {
                  if (!data) return null;
                  return (
                    <div style={{ textAlign: 'center' }}>
                      <WarningIcon
                        size={14}
                        style={{ verticalAlign: 'text-bottom', color: colors.PUMPKIN_ORANGE }}
                      />
                    </div>
                  );
                },
              },
            ].concat(this.columns);
          }

          return this.columns;
        };
      }
    )
  )
);
